import { PostEntity } from 'mk2/apps/forum/schemas';
import { initialComplaintsState, ComplaintsState } from 'mk2/containers/Complaint/Complaint.reducers';
import {
    complaintKey,
    ComplaintEntity,
    ComplaintSchema,
    ComplaintTargetEntity,
    ComplaintTargetType,
    ComplaintWithoutTargetEntity,
} from 'mk2/containers/Complaint/Complaint.schemas';
import { memoizeLRU, memoizeOne } from 'mk2/helpers/cache';
import { AppState } from 'mk2/reducers';
import { EntitiesState } from 'mk2/reducers/entities';
import { BlogPostEntity, CommentEntity } from 'mk2/schemas';
import { findDenormalizedEntitiesByCond } from 'mk2/selectors';

export const getComplaintsState = (state: AppState): ComplaintsState =>
    state.containers.global.complaint || initialComplaintsState;

export const getComplaintShownForTarget = memoizeOne(
    (complaintsState: ComplaintsState, targetType: ComplaintTargetType, targetId: number): boolean => {
        const key = complaintKey(targetType, targetId);
        return complaintsState[key] ? complaintsState[key].show : false;
    },
);

export const getComplaintShownForForumPost = (state: AppState, post: PostEntity): boolean =>
    !!post && getComplaintShownForTarget(getComplaintsState(state), ComplaintTargetType.ForumThread, post.id);

export const getComplaintShownForBlogPost = (state: AppState, post: BlogPostEntity): boolean =>
    !!post && getComplaintShownForTarget(getComplaintsState(state), ComplaintTargetType.PhotoblogPost, post.id);

const getComplaintsShownForTargets = memoizeLRU(50,
    (complaintsState: ComplaintsState, targetType: ComplaintTargetType, targets: ComplaintTargetEntity[]): boolean[] =>
        targets.map((target) => {
            const key = complaintKey(targetType, target.id);
            return complaintsState[key] ? complaintsState[key].show : false;
        }),
);

export const getComplaintsShownForBlogPosts = (state: AppState, posts: BlogPostEntity[]): boolean[] =>
    getComplaintsShownForTargets(getComplaintsState(state), ComplaintTargetType.PhotoblogPost, posts);

export const getComplaintsShownForBlogComments = (state: AppState, comments: CommentEntity[]): boolean[] =>
    getComplaintsShownForTargets(getComplaintsState(state), ComplaintTargetType.PhotoblogComment, comments);

export const getComplaintsShownForForumComments = (state: AppState, comments: CommentEntity[]): boolean[] =>
    getComplaintsShownForTargets(getComplaintsState(state), ComplaintTargetType.ForumMessage, comments);

export const getComplaintsNonDeleted = memoizeOne(
    <T extends { deletedAt: string; }>(complaints: T[]): T[] => complaints.filter((complaint) => !complaint.deletedAt),
);

export const findComplaintsNonDeleted = (state: AppState, targetEntityType: ComplaintTargetType, targetId: number): ComplaintEntity[] =>
    findDenormalizedEntitiesByCond<ComplaintEntity>(
        state,
        ComplaintSchema,
        `cacheKey-${findComplaintsNonDeleted.name}-${targetEntityType}-${targetId}`,
        (normalizedComplaint: any) => {
            return (
                normalizedComplaint.target && // complaint target may be undefined if not fetched (for example complaint of post's comment)
                normalizedComplaint.target.id === targetId &&
                normalizedComplaint.target.schema === targetEntityType &&
                !normalizedComplaint.deletedAt
            );
        },
    );

export const getComplaintsComplaintsCounts = (entities: EntitiesState, entity: string, ids: number[]) => {
    if (!entities[ComplaintSchema.key]) {
        return null;
    }

    const complaints = entities[ComplaintSchema.key] as { [id: string]: ComplaintWithoutTargetEntity };
    const filteredComplaints = Object.keys(complaints).filter((key) => {
        return complaints[key].target.schema === entity && ids.includes(complaints[key].target.id) && !complaints[key].deletedAt;
    });

    return filteredComplaints.length;
};
